import { PortalNoteMedia } from "@/views/notes/model";

export enum DiscussionType {
    Comment = "comment",
    Event = "event",
}

export type DiscussionMedia = PortalNoteMedia;

export interface DiscussionBase {
    id: number;
    author: { id: number; name: string; photo: object };
    bookmark?: string;
    createdAt: number;
    updatedAt: number;
    readonly?: boolean;
    userHasReported?: boolean;
    media: DiscussionMedia[];
}

export class DiscussionBase {
    id: number;
    author: { id: number; name: string; photo: object };
    bookmark?: string;
    createdAt: number;
    updatedAt: number;
    readonly?: boolean;
    permissions: {
        canDelete: boolean;
        canEdit: boolean;
    };
    userHasReported?: boolean;
    media: DiscussionMedia[];

    constructor(
        id: number,
        author: { id: number; name: string; photo: object },
        bookmark: string | undefined,
        createdAt: number,
        updatedAt: number,
        permissions: {
            canDelete: boolean;
            canEdit: boolean;
        },
        userHasReported?: boolean,
        media?: DiscussionMedia[]
    ) {
        this.id = id;
        this.author = author;
        this.bookmark = bookmark;
        this.createdAt = createdAt;
        this.updatedAt = updatedAt;
        this.permissions = permissions;
        this.userHasReported = userHasReported || false;
        this.media = media || [];
    }
}

export interface Comment extends DiscussionBase {
    body: string;
    replies: Comment[];
    readonly: boolean;
    type?: string;
}

export class Comment extends DiscussionBase {
    body: string;
    replies: Comment[];
    readonly: boolean;

    constructor(
        id: number,
        author: { id: number; name: string; photo: object },
        bookmark: string | undefined,
        body: string,
        createdAt: number,
        updatedAt: number,
        permissions: {
            canDelete: boolean;
            canEdit: boolean;
        },
        userHasReported?: boolean,
        media?: DiscussionMedia[]
    ) {
        super(id, author, bookmark, createdAt, updatedAt, permissions, userHasReported, media);
        this.body = body;
        this.replies = [];
        this.readonly = true;
        this.type = DiscussionType.Comment;
    }
}

export interface NewComment {
    projectId: number;
    bookmark: string;
    body: string;
    threadId?: number;
    mediaIds?: number[];
}

export interface DataEvent extends DiscussionBase {
    title: string;
    description: string;
    start: number;
    end: number;
    readonly?: boolean;
    type?: string;
}

export class DataEvent extends DiscussionBase {
    title: string;
    description: string;
    start: number;
    end: number;
    readonly?: boolean;
    type?: string;

    constructor(
        id: number,
        author: { id: number; name: string; photo: object },
        bookmark: string | undefined,
        createdAt: number,
        updatedAt: number,
        title: string,
        description: string,
        start: number,
        end: number,
        permissions: {
            canDelete: boolean;
            canEdit: boolean;
        },
        userHasReported?: boolean,
        media?: DiscussionMedia[]
    ) {
        super(id, author, bookmark, createdAt, updatedAt, permissions, userHasReported, media);
        this.title = title;
        this.description = description;
        this.start = start;
        this.end = end;
        this.readonly = true;
        this.type = DiscussionType.Event;
    }
}

export interface NewDataEvent extends NewComment {
    title: string;
    description: string;
    allProjectSensors?: boolean;
    start: number;
    end: number;
}

export function commentFromApi(post: {
    id: number;
    author: { id: number; name: string; photo: object };
    bookmark?: string;
    body: string;
    createdAt: number;
    updatedAt: number;
    permissions: { canDelete: boolean; canEdit: boolean };
    userHasReported?: boolean;
    media?: DiscussionMedia[];
    replies?: ReturnType<typeof commentFromApi>[];
}): Comment {
    const comment = new Comment(
        post.id,
        post.author,
        post.bookmark,
        post.body,
        post.createdAt,
        post.updatedAt,
        post.permissions,
        post.userHasReported,
        post.media
    );
    if (post.replies) {
        comment.replies = post.replies.map((r) => commentFromApi(r));
    }
    return comment;
}

export function dataEventFromApi(event: {
    id: number;
    author: { id: number; name: string; photo: object };
    bookmark?: string;
    createdAt: number;
    updatedAt: number;
    title: string;
    description: string;
    start: number;
    end: number;
    permissions: { canDelete: boolean; canEdit: boolean };
    userHasReported?: boolean;
    media?: DiscussionMedia[];
}): DataEvent {
    return new DataEvent(
        event.id,
        event.author,
        event.bookmark,
        event.createdAt,
        event.updatedAt,
        event.title,
        event.description,
        event.start,
        event.end,
        event.permissions,
        event.userHasReported,
        event.media
    );
}

export enum CommentsErrorsEnum {
    getComments = "Something went wrong loading the comments",
    postComment = "Something went wrong saving your comment.",
    deleteComment = "Something went wrong deleting your comment.",
    editComment = "Something went wrong editing your comment. Your changes will are not saved.",
}

export enum DataEventsErrorsEnum {
    getDataEvents = "Something went wrong loading the events",
    postDataEvent = "Something went wrong saving your event.",
    deleteDataEvent = "Something went wrong deleting your event.",
    editDataEvent = "Something went wrong editing your event. Your changes will are not saved.",
}
export enum FiledNotesErrorsEnum {
    getFiledNotes = "Something went wrong loading the notes",
}
